import type {
  ForwardedRef,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import type { TooltipPreference } from '../../Tooltip/Tooltip';
import type { MLPVersion, Theme } from '../../types/enums';
import type { Data } from '../../DropDown/types/propsType';
import type {
  CalendarWidth,
  CalendarYearDropdownType,
  DatePickerType,
  DatePickerValidataionStatus,
  TimePickerModal,
  DateFormat,
  CalendarType,
  SelectorType,
} from './enums';

export type CalendarTheme = Theme.ACTION | Theme.PURPLE | Theme.ORANGE;

export type NavigationButtonDisable = {
  disableLeft: boolean;
  disableRight: boolean;
};
export interface ScheduleEventType {
  e: ReactKeyboardEvent | ReactMouseEvent;
  dateTime?: Date;
  selectRange: boolean;
  dateTimeTo: Date;
  startTime?: string;
  endTime?: string;
  onChange?: (
    dateTime?: Date,
    dateTimeTo?: Date,
    startTime?: string,
    endTime?: string,
    e?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
  ) => void;
  setScheduledDateTime: (date?: DateObj) => void;
  setScheduledDateTimeTo: (date: DateObj) => void;
  handleCalendarVisibility: (e: ReactKeyboardEvent | ReactMouseEvent, show: boolean) => void;
  isTimePickerOpen?: boolean;
  is24HoursFormat?: boolean;
}
export interface DateTimeTextType {
  scheduledDateTime?: DateObj;
  scheduledDateTimeTo: DateObj;
  showTime: boolean;
  selectRange: boolean;
}
export interface DateObj {
  year: number;
  month: number;
  date: number;
  time?: string;
}

export interface RefType {
  closeTimePickerModal: () => void;
  isTimePickerOpen: () => boolean;
  timePickerModal: () => TimePickerModal;
}
export type MonthYearSelectorComponentProptype = {
  isOnlyMonthYearSelector: boolean;
  activeSelector: SelectorType;
  displayMonth: string;
  displayYear: string;
  navigate: (event?: ReactKeyboardEvent | ReactMouseEvent, direction?: 'left' | 'right', currentYear?: number) => void;
  onSelectorClick: (origin: SelectorType) => void;
};
export type CalendarMonthYearComponentPropType = {
  displayMonth: string;
  displayYear: string;
  showYearDropdown?: CalendarYearDropdownType;
  yearDropdownData?: Data[];
  yearDropdownConfigData?: {
    label: string;
    onSelect: (value: Data) => unknown;
    /**
     * @description set default year in dropdown. Ex. 2007.
     */
    defaultYear?: number;
  };
  yearList: { id: string; name: number }[];
  navigate: (event?: ReactKeyboardEvent | ReactMouseEvent, direction?: 'left' | 'right', currentYear?: number) => void;
};
export interface CalendarProps {
  disableDate?: (e: Date) => boolean;
  disableMonth?: (month: string, year?: number) => boolean;
  disableYear?: (year: number) => boolean;
  disableTime?: (time: string, date: Date) => boolean;
  showFullCalendar?: boolean;
  show31DayCalendar?: boolean;
  onChange?: (
    dateTime?: Date,
    dateTimeTo?: Date,
    startTime?: string,
    endTime?: string,
    e?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
  ) => void;
  disabledDates?: Date[];
  disabledPastDates?: boolean;
  disablePastTime?: boolean;
  timeLabel: string;
  timeLabelStart: string;
  timeLabelEnd: string;
  showTime?: boolean;
  cancelLabel?: string;
  scheduleLabel?: string;
  selectRange?: boolean;
  onNavigate?: (e?: ReactKeyboardEvent | ReactMouseEvent) => void;
  value?: Date;
  valueTo?: Date;
  startTimeValue?: string;
  endTimeValue?: string;
  forwardedRef?: ForwardedRef<HTMLDivElement>;
  ref?: ForwardedRef<HTMLDivElement | RefType>;
  ariaLabel?: string;
  calendarTitle?: string;
  calendarHeading?: string;
  /**
   * @description calendar will only show 28 days
   */
  show4WeeksOnly?: boolean;
  /**
   * @description calendar and timepicker type
   */
  type?: DatePickerType;
  is24HoursFormat?: boolean;
  theme?: CalendarTheme;
  navigationButtonDisable?: NavigationButtonDisable;
  scheduleEventOnDates?: Date[];
  onClose?: (e?: ReactKeyboardEvent | ReactMouseEvent) => void;
  showYearDropdown?: CalendarYearDropdownType;
  yearDropdownData?: Data[];
  yearDropdownConfigData?: {
    label: string;
    onSelect: (value: Data) => unknown;
    disableItemKey?: string;
    /**
     * @description set default year in dropdown. Ex. 2007.
     */
    defaultYear?: number;
  };
  useReactPortal?: boolean;
  portalContainerId?: string;
  /**
   * This will hide calendar title or month container when enabled
   *
   * @type boolean
   * @default false
   * @optional
   */
  hideCalendarTitle?: boolean;
  calendarType?: CalendarType;
  autoCloseMonthYearSelector?: boolean;
}
type DatePickerBaseProps = Omit<CalendarProps, 'ref' | 'calendarType'> & {
  inputLabel?: string;
  labelInfo?: string;
  labelInfoPreference?: TooltipPreference;
  placeholder?: string;
  cancelLabel: string;
  scheduleLabel: string;
  isWithinModal?: boolean;
  isWithinDrawer?: boolean;
  onCalendarToggle?: (e: ReactKeyboardEvent | ReactMouseEvent) => void;
  onSelect?: (e?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent, selectedDate?: Date) => void;
  displayText?: string;
  description?: string;
  calendarHeading?: string;
  calendarFooter?: ReactNode;
  inputContainerHeight?: string;
  ariaLabel?: string;
  calendarWidth?: CalendarWidth;
  ref?: ForwardedRef<HTMLDivElement>;
  disabled?: boolean;
  status?: DatePickerValidataionStatus;
  disableDate?: (e: Date) => boolean;
  required?: boolean;
  mlpVersion?: MLPVersion;
  compact?: boolean;
};

/**
 * When calendarType is ONLY_MONTH_YEAR_SELECTOR, dateFormat is not allowed
 * as the component internally uses YYYY/MM format.
 */
type OnlyMonthYearSelectorProps = DatePickerBaseProps & {
  calendarType: typeof CalendarType.ONLY_MONTH_YEAR_SELECTOR;
  dateFormat?: never;
};

type DefaultCalendarProps = DatePickerBaseProps & {
  calendarType?: Exclude<CalendarType, typeof CalendarType.ONLY_MONTH_YEAR_SELECTOR>;
  /**
   * To select date format displayed on date picker
   * @type DateFormat
   * @default DateFormat.DDMMYYYY
   * @optional
   */
  dateFormat?: DateFormat;
};

export type DatePickerProps = OnlyMonthYearSelectorProps | DefaultCalendarProps;
export const DAY_LIMIT = 28;
export const PLACEHOLDER_TEXT = 'placeholderText';

export type KeyEvent = ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent;
export type HandleStartAndEndSelectionProps = {
  currentSelectedDataTime: Date;
  selectedDateTime: Date;
  selectedDateTimeEnd: Date;
  setSelectedDateTime: React.Dispatch<React.SetStateAction<Date>>;
  setSelectedDateTimeEnd: React.Dispatch<React.SetStateAction<Date>>;
  setSelectedDateTimeEndHover: React.Dispatch<React.SetStateAction<Date>>;
  setIsDateRangeChanged: React.Dispatch<React.SetStateAction<boolean>>;
  minValue: Date;
  e: KeyEvent;
  onChange?: (
    dateTime?: Date,
    dateTimeTo?: Date,
    startTime?: string,
    endTime?: string,
    e?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
  ) => void;
};

export interface WeekSpanProps {
  displayDate: { date: number; month: number } | false;
  isSelected: { date: number; month: number } | false;
  isDateRangeChanged: boolean;
  isSelectedTo?: boolean;
  isWithinSelectedRange: boolean;
  isDisabled: boolean;
  j: number;
  i: number;
  onClickHandler: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  onKeyDownHandler: (e: ReactKeyboardEvent<HTMLSpanElement>) => void;
  onMouseOverHandler: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  onFocusHandler: (e: React.FocusEvent<HTMLSpanElement>) => void;
  ariaLabel: string;
  scheduleEventOnDates?: Date[];
  year: number;
}

export type TimePickerProps = {
  title: string;
  is24HoursFormat?: boolean;
  value?: string;
  onTimeChange?: (time: string) => void;
  disablePastTime?: boolean;
  // disableTime property will have access to time only as timePicker component works upon time only.
  disableTime?: (time: string) => boolean;
};

// calendarDayMap Props
export interface CalendarDaysMappingInterface {
  baseDate: Date | undefined;
  baseEndDate: Date | undefined;
  intermediateDate: Date | undefined;
  intermediateEndDate: Date | undefined;
  showFullCalendar: boolean;
  show31DayCalendar: boolean;
  show4WeeksOnly: boolean;
  handleDateTimeUpdate: (
    event: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    currentDate: Date,
    isMouseEvent: boolean,
  ) => void;
  selectRange: boolean;
  scheduleEventOnDates: Date[] | undefined;
  disableDate?: (e: Date) => boolean;
  disabledDates?: Date[];
  disabledPastDates?: boolean;
  ariaLabel?: string;
  defaultYear?: number;
}
export type ComponentUserEvent = {
  onClick: (e: ReactMouseEvent<HTMLInputElement | HTMLButtonElement>) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>) => void;
};
